import React, { useState, useEffect, createRef, useContext } from 'react';
import { Row, Col } from '@thd-olt-component-react/grid';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { getDomPath } from '@thd-olt-functional/utils';
import { string, bool } from 'prop-types';
import './track-out-of-stock.style.scss';
import { TextField, Typography, Button } from '@one-thd/sui-atomic-components';
import { Formik } from 'formik';
import { TextSignup } from './TextSignup';

const MESSAGE_STATES = {
  contact_missing: {
    type: 'error',
    alertClass: 'danger',
    form: { enabled: true },
    ariaInvalid: true,
    title: 'An error has occurred.',
    message: 'Please enter an email or phone number to continue.',
  },
  email_missing: {
    type: 'error',
    form: { enabled: true },
    ariaInvalid: true,
  },
  phone_missing: {
    type: 'error',
    form: { enabled: true },
    ariaInvalid: true
  },
  failure: {
    type: 'error',
    alertClass: 'danger',
    title: 'An error has occurred.',
    message: 'Please resubmit your contact information.',
    form: { enabled: true },
    ariaInvalid: true
  },
  duplicate: {
    type: 'success',
    alertClass: 'success',
    title: 'Thank you!',
    message: 'You are already signed up to receive a notification if this product is back in stock.',
    form: { enabled: false },
    ariaInvalid: true
  },
  success: {
    type: 'success',
    alertClass: 'success',
    title: 'Thank you!',
    message: 'We\'ll send you a notification if this product is back in stock.',
    form: { enabled: false },
    ariaInvalid: false
  },
  init: {
    type: '',
    alertClass: '',
    title: '',
    message: '',
    form: { enabled: true },
    ariaInvalid: false
  }
};

const INHOUSE_STATUS_MAP = {
  Success: 'success',
  OPS_ERROR_200: 'email_missing',
  OPS_ERROR_201: 'duplicate',
  OPS_ERROR_203: 'failure',
  OPS_ERROR_204: 'failure',
  OPS_ERROR_205: 'failure',
  OPS_DECRYPT_ERROR: 'failure'
};

const TrackOutOfStock = ({ itemId, enableSms, legalDisclaimer }) => {
  const ref = createRef();
  const [state, setState] = useState({
    status: 'init',
    loader: false
  });
  const { hosts, path } = useContext(ExperienceContext);
  const { status } = state;
  const trackError = (domPath) => {
    const message = MESSAGE_STATES[status]?.message;
    window.LIFE_CYCLE_EVENT_BUS.trigger('track-out-of-stock.click', { message, domPath });
  };

  useEffect(() => {
    if (['init', 'success', 'duplicate'].includes(status)) {
      return;
    }
    const _path = getDomPath(ref);
    trackError(_path);
  }, [status]);

  const prepareInhouseOptions = (values) => {
    const email = values.email || '';
    const sms = values.sms.replace(/\D/g, '') || '';
    return {
      method: 'POST',
      crossdomain: false,
      headers: { 'Content-Type': 'application/json' },
      dataType: 'json',
      body: JSON.stringify({ emailId: email, phoneNumber: sms, omsId: itemId })
    };
  };

  const prepareExternalUrl = () => {
    const { trackif = '' } = hosts || {};
    return `${trackif}/tools/auto`;
  };

  const prepareExternalOptions = (values) => {
    const { origin, pathname } = (typeof window !== 'undefined' && window.location) || {};
    const itemURL = `${origin}${pathname}`;
    const formData = new FormData();
    formData.append('email', values.email);
    formData.append('url', typeof window !== 'undefined' ? itemURL : path);
    formData.append('preset', 'out-of-stock');

    return {
      method: 'POST',
      crossdomain: true,
      dataType: 'json',
      body: formData
    };
  };

  const prepareNotificationData = (values) => {
    if (itemId) {
      return {
        url: '/discovery/opt/in',
        options: prepareInhouseOptions(values)
      };
    }

    if (path) {
      return {
        url: prepareExternalUrl(),
        options: prepareExternalOptions(values)
      };
    }

    setState({
      ...state,
      loader: false,
      status: 'failure'
    });
    return null;
  };

  const sendNotification = async ({ url, options }) => {
    try {
      const response = await fetch(url, options).then((resp) => resp.json());
      const newStatus = itemId ? INHOUSE_STATUS_MAP[response.message] : response.status;
      setState({
        ...state,
        loader: false,
        status: newStatus || 'failure'
      });
    } catch {
      setState({
        ...state,
        loader: false,
        status: 'failure'
      });
    }
  };

  const submitForm = async (values) => {
    const notificationData = prepareNotificationData(values);
    if (!notificationData) {
      return;
    }
    await sendNotification(notificationData);
  };

  const validateForm = (values) => {
    const errors = {};
    if (!values.email && !values.sms && enableSms) {
      errors.email = 'Please provide your email address, formatted as you@domain.com.';
      errors.sms = 'Please provide your phone number.';
      setState({
        ...state,
        loader: false,
        status: 'contact_missing'
      });
    }
    if (values.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = 'Please provide a valid email address.';
      setState({
        ...state,
        loader: false,
        status: 'email_missing'
      });
    }
    if (values.sms.length !== 14 && values.sms) {
      errors.sms = 'Phone number must be 10 digits.';
      setState({
        ...state,
        loader: false,
        status: 'phone_missing'
      });
    }
    return errors;
  };

  return (
    <Formik
      initialValues={{ email: '', sms: '' }}
      validate={(values) => validateForm(values)}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(async () => {
          await submitForm(values);
          setSubmitting(false);
        }, 400);
      }}
    >
      {(formikProps) => {
        const {
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting
        } = formikProps;
        return (
          <Row className="track-out-of-stock" data-component="TrackOutOfStock">
            <Col lg="12">
              <Typography variant="body-base">
                Receive an email {enableSms ? 'and/or text notification' : '' } when this item is back in stock.
              </Typography>
            </Col>
            <Col lg="12">
              {status && (
                <form
                  onSubmit={handleSubmit}
                >
                  <div className="sui-w-full sm:sui-max-w-72 sui-grid sui-grid-cols-1 sui-gap-y-6">
                    <TextField
                      onChange={handleChange}
                      id="email"
                      status={(errors.email ? 'error' : values.email && 'success')}
                      statusMessage={errors.email}
                      placeholder="name@homedepot.com"
                      value={values.email}
                      label="Email"
                      optional={enableSms}
                      fullWidth
                    />

                    { enableSms && (
                      <TextSignup
                        textValue={values.sms}
                        onChange={handleChange}
                        status={(errors.sms ? 'error' : values.sms && 'success')}
                        statusMessage={errors.sms}
                        optional
                      />
                    )}

                    <Button
                      ref={ref}
                      aria-label="Submit"
                      disabled={isSubmitting}
                      type="submit"
                    >
                      Notify Me
                    </Button>
                  </div>
                </form>
              )}
              {status
                && (
                  <div className="track-out-of-stock__status">
                    <div className={`alert-inline alert-inline--${MESSAGE_STATES[status].alertClass}`}>
                      <span className="alert-inline__message" aria-live="polite">
                        <span className="alert-inline__title">{MESSAGE_STATES[status].title} </span>
                        {MESSAGE_STATES[status].message}
                      </span>
                    </div>
                  </div>
                )}
            </Col>
            {enableSms
              && (
                <Col lg="12">
                  <Typography variant="body-xs" height="tight">
                    {legalDisclaimer || `By clicking “Notify Me,” you consent to the
                      Home Depot contacting you at the
                      number provided when your item is back in stock,
                      including through the use of automated technology, SMS/MMS, and/or prerecorded, AI,
                      or artificial messages (“Back In Stock Texting Program”). Messaging frequency varies.
                      Consent is not a condition of purchase, and you may opt out of the Back In Stock Texting
                      Program messages at any time by replying with your 5-digit code provided at Sign-Up
                      Confirmation to stop texts for this item, STOP to stop texts for all your open orders or
                      ENDALL to stop all texting programs from HD. You may also opt out by calling 1-800-HOMEDEPOT.
                      Msg & data rates may apply. You also agree to Home Depot Privacy & Security Statement.`}
                  </Typography>
                </Col>
              )}
          </Row>
        );
      }}
    </Formik>
  );
};

TrackOutOfStock.displayName = 'TrackOutOfStock';

TrackOutOfStock.dataModel = {};

TrackOutOfStock.propTypes = {
  itemId: string,
  enableSms: bool,
  legalDisclaimer: string
};

TrackOutOfStock.defaultProps = {
  itemId: null,
  enableSms: false,
  legalDisclaimer: null
};

export { TrackOutOfStock };
